// bubbleProvider.js
import { BubbleClient } from "./bubbleClient.js";
import { FileHelper } from "./fileHelper.js";
import { utility } from "./utility.js";
import { UploadProgressTracker } from "./progressForm.js";

export class BubbleProvider {
  constructor() {
    this.client = new BubbleClient();
    this._companies = [];
    this._folders = [];
    this._contacts = [];
  }

  async getAllUsers() {
    const result = await this.client.bubbleKeyValuePairs("User", "Full name");
    return result.sort((a, b) => a.value.localeCompare(b.value));
  }

  async getAllCompanies(userId) {
    const constraints = JSON.stringify([
      { key: "Key Accounts", constraint_type: "contains", value: userId },
    ]);
    this._companies = await this.client.getAllBubbleRecordsByFilter("Company", constraints, [
      "Name",
      "Key Accounts",
    ]);
    return this._companies
      .filter((r) => r)
      .map((r) => this.toKeyValModel(r, "Name"))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  async getAllFolders(companyName) {
    const constraints = JSON.stringify([
      { key: "Company", constraint_type: "equals", value: companyName },
    ]);
    this._folders = await this.client.getAllBubbleRecordsByFilter("Folder", constraints, [
      "Name",
      "Company",
    ]);
    return this._folders
      .filter((r) => r)
      .map((r) => this.toKeyValModel(r, "Name"))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  async getAllContacts(companyName) {
    const constraints = JSON.stringify([
      { key: "Company", constraint_type: "equals", value: companyName },
    ]);
    this._contacts = await this.client.getAllBubbleRecordsByFilter("Contact", constraints, [
      "Name",
      "Company",
    ]);
    return this._contacts
      .filter((r) => r)
      .map((r) => this.toKeyValModel(r, "Name"))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  async getAllInsuranceCompanies() {
    const emptyConstraints = "[]";
    const records = await this.client.getAllBubbleRecordsByFilter(
      "InsuranceCompany",
      emptyConstraints,
      ["Name"]
    );
    if (!records) return [];
    return records
      .filter((r) => r)
      .map((r) => this.toKeyValModel(r, "Name"))
      .sort((a, b) => a.value.localeCompare(b.value));
  }

  async doesEmailExist(mailItem) {
    try {
      return await this.client.isEmailExist("Email", mailItem);
    } catch (ex) {
      console.error("Error checking email existence:", ex);
      return false;
    }
  }

  /**
   * Returns the full Bubble record for the given mail item, or null if not found.
   * The record shape mirrors BubbleRecord from the C# side:
   *   { _id, id, status, ...allOtherFields }
   */
  async getEmailFromBubble(mailItem) {
    try {
      return await this.client.getEmailRecord("Email", mailItem);
    } catch (ex) {
      console.error(
        "Error fetching email record from Bubble:",
        utility.getMailUniqueKey(mailItem),
        ex
      );
      return null;
    }
  }

  async saveEmail(mailModel, type) {
    const progressTracker = new UploadProgressTracker();
    
    try {
      progressTracker.start();

      const mail = mailModel.mailItem;
      const mailUniqueKey = utility.getMailUniqueKey(mail);
      const blob = await this.getMailAsBlob(mail);

      // ---------------- EMAIL UPLOAD (0–50) ----------------
      progressTracker.setProgress(10, "Preparing email for upload...");

      const downloadLink = await FileHelper.uploadMailToFirebase(blob, mailModel.subject, (progress) => {
        if (progress === -1) {
          progressTracker.error("Failed to upload email to Firebase");
        } else {
          progressTracker.setProgress(progress, "Uploading email file...");
        }
      });

      if (!downloadLink) {
        progressTracker.error("Failed to upload email to Firebase");
        return false;
      }

      progressTracker.setProgress(50, "Email uploaded successfully");

      // ---------------- ATTACHMENTS (50–80) ----------------
      let attachments = [];
      const mailAttachments = mail.attachments || [];
      
      if (mailAttachments.length > 0) {
        const total = mailAttachments.length;
        for (let i = 1; i <= total; i++) {
          const att = mailAttachments[i - 1];
          const uploaded = await FileHelper.uploadAttachmentToFirebase(
            att, 
            (progress) => {
              if (progress === -1) {
                progressTracker.error(`Failed to upload attachment to Firebase: ${att.name}`);
              }
            },
            i, 
            total
          );
          attachments.push(uploaded);

          const percent = 50 + Math.round((i / total) * 30);
          progressTracker.setProgress(percent, `Uploading attachment ${i} of ${total}: ${att.name}`);
        }
      } else {
        progressTracker.setProgress(80, "No attachments to upload");
      }

      // ---------------- PREPARE DATA ----------------
      progressTracker.setProgress(85, "Preparing email data...");

      const receivedTime = mail.dateTimeCreated ? new Date(mail.dateTimeCreated).toISOString() : "";
      const allRecipients = await utility.getAllRecipients();
      const receiver = utility.getRecipients(allRecipients, 0);
      const bodyContent = await utility.getBodyAsText(mail);

      const fields = {
        Company: mailModel.company || "",
        "Company contact": mailModel.contact || "",
        "Download Link": downloadLink || "",
        "Email attachments txt": attachments.length > 0 ? attachments : [],
        "Email Content": bodyContent,
        Subject: mailModel.subject || "",
        "Email ID": mail.itemId || "",
        Folder: mailModel.folder || "",
        "Insurance company": mailModel.insuranceCompany || "",
        isBackofficeKeyAccount: mailModel.isBusinessCritical || false,
        "Key Account": mailModel.userId || "",
        "Mail unique key": mailUniqueKey,
        "Media url": downloadLink || "",
        "Receive date": receivedTime,
        Receiver: receiver,
        Recipients: [receiver],
        Sender: utility.getSenderSmtpAddress(mail.from),
        isBusinessCritical: mailModel.isBusinessCritical || false,
      };

      // Extract CC recipients
      const ccRecipients = [];
      if (mail.cc) {
        mail.cc.forEach((r) => ccRecipients.push(r.emailAddress || r.address || ""));
      }
      if (ccRecipients.length > 0) {
        fields["CC recipients"] = ccRecipients;
      }

      // ---------------- SAVE TO BUBBLE (80–100) ----------------
      progressTracker.setProgress(90, "Saving to database...");

      const { record, success } = await this.client.createBubbleRecord("email", fields);

      if (!success) {
        console.error("[BubbleProvider] Save email failed:", {
          model: mailModel,
          response: record,
        });
        progressTracker.error("Failed to save email to database");
        return false;
      }

      progressTracker.complete();
      return success;
    } catch (ex) {
      console.error("[BubbleProvider] Error in saveEmail catch block:", ex);
      progressTracker.error("An error occurred during upload: " + ex.message);
      return false;
    }
  }

  async uploadEmailToBubble(mail, mailModel, record) {
    try {
      const blob = await this.getMailAsBlob(mail);
      console.log("uploadEmailToBubble", blob);

      const downloadLink = await FileHelper.uploadMailToBubble(blob, mailModel.subject);
      console.log(downloadLink);
      if (!downloadLink) {
        console.error("Download link is empty for", mailModel);
        utility.ErrorMsg("Failed to upload mail to server.");
        return;
      }
      const attachments = await this.getAttachmentFiles(mail.attachments);
      const fields = {
        "Download Link": downloadLink,
        "Media url": downloadLink,
        "Email attachments txt": attachments.length > 0 ? attachments : [],
      };
      // For updates, the Data API is still used
      await this.client.updateBubbleRecord("Email", fields, record.id);
    } catch (ex) {
      console.error("Error uploading email to Bubble:", ex);
    }
  }
  async getMailAsBlob(mailItem) {
    console.log("getting blob");

    return new Promise((resolve, reject) => {
      mailItem.getAsFileAsync((result) => {
        console.log("getAsFileAsync result:", result);

        if (result.status !== Office.AsyncResultStatus.Succeeded) {
          reject(result.error);
          return;
        }

        const base64 = result.value; // ✅ THIS IS BASE64 STRING

        try {
          // Convert base64 → binary
          const byteCharacters = atob(base64);
          const byteNumbers = new Array(byteCharacters.length);

          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }

          const byteArray = new Uint8Array(byteNumbers);

          const blob = new Blob([byteArray], {
            type: "application/vnd.ms-outlook",
          });

          console.log("blob created successfully");

          resolve(blob);
        } catch (err) {
          reject(err);
        }
      });
    });
  }
  async getAttachmentFiles(attachments, progressCallback = null) {
    const files = [];
    for (let i = 0; i < attachments.length; i++) {
      const att = attachments[i];
      const link = await FileHelper.uploadAttachmentToFirebase(
        att, 
        progressCallback, 
        i + 1, 
        attachments.length
      );
      files.push(link);
    }
    return files;
  }
  toKeyValModel(record, fieldName) {
    const key = record._id || record.id; // Bubble uses _id sometimes
    const value = record[fieldName] || ""; // field is at top-level
    return { key, value };
  }

  // toKeyValModel(record, fieldName) {
  //   return { key: record.id, value: record.fields[fieldName] };
  // }

  // Add Folder
  async addFolderToBubble(fields) {
    try {
      // Call your Bubble client to create a record
      const { record, success } = await this.client.createBubbleRecord("Folder", fields);

      // Handle failure
      if (!success || !record) {
        return null;
      }
      // Extract "Name" field value safely
      const value = record.fields?.Name ?? "";

      // Return the equivalent of KeyValModel
      return { key: record.id, value };
    } catch (error) {
      console.error("Error adding folder to Bubble:", error);
      return null;
    }
  }
  async addContactToBubble(fields) {
    try {
      // Call your Bubble client to create a record
      const { record, success } = await this.client.createBubbleRecord("Contact", fields);

      // Handle failure
      if (!success || !record) {
        return null;
      }
      // Extract "Name" field value safely
      const value = record.fields?.Name ?? "";

      // Return the equivalent of KeyValModel
      return { key: record.id, value };
    } catch (error) {
      console.error("Error adding contact to Bubble:", error);
      return null;
    }
  };
  showMessage(message, type = "info") {
    const container = document.getElementById("messageContainer");
    if (!container) return;

    container.textContent = message;
    container.className = `message ${type}`;
    container.style.display = "block";

    setTimeout(() => {
      container.style.display = "none";
    }, 3000);
  }
}
